import React, { useState, useEffect } from 'react';
import { Popup, Switch, Toast } from 'antd-mobile';
import { InformationCircleOutline } from 'antd-mobile-icons';
import styled, { ThemeProvider } from 'styled-components';
import { packService } from '../../services/api';
import type { ScooterPreferences } from '../../services/api/types';
import { r, riderLight } from '../../theme/riderTheme';
import {
  CloseButton,
  DragHandle,
  Sheet,
} from '../../theme/sheetPrimitives';
import {
  ButtonChevron,
  GhostButton,
  PrimaryButton,
} from '../../theme/surfacePrimitives';

const Container = styled.div`
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
  padding: 14px 16px 12px;
  margin-bottom: 14px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
`;

const InfoTrigger = styled.button.attrs({ type: 'button' as const })`
  background: transparent;
  border: 0;
  padding: 2px;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => r(theme).text.faint};
  cursor: pointer;
  transition: color 160ms ease;

  &:hover {
    color: ${({ theme }) => r(theme).text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
    border-radius: 4px;
  }
`;

const Description = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => r(theme).text.muted};
  line-height: 1.5;
  margin-bottom: 8px;
`;

const StatusRow = styled.div<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme, $enabled }) =>
    $enabled ? r(theme).accent.ok : r(theme).accent.alert};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme, $enabled }) =>
      $enabled ? r(theme).accent.ok : r(theme).accent.alert};
    box-shadow: 0 0 6px
      ${({ theme, $enabled }) =>
        $enabled ? r(theme).accent.ok : r(theme).accent.alert};
  }
`;

// antd-mobile Switch's --checked-color points at its default antd-blue;
// override here so the toggle reads as midnight/lime when enabled.
const ThemedSwitch = styled(Switch)`
  --checked-color: ${({ theme }) => r(theme).cta.bg};
  --height: 26px;
  --width: 46px;
`;

const SheetEyebrow = styled.div<{ $tone?: 'alert' | 'neutral' }>`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme, $tone }) =>
    $tone === 'alert' ? r(theme).accent.alert : r(theme).text.faint};
  margin: 8px 0 10px;
`;

const SheetTitle = styled.h2`
  margin: 0 0 10px;
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => r(theme).text.primary};
`;

const SheetBody = styled.p`
  margin: 0 0 22px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => r(theme).text.muted};
`;

const SheetFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 6px;
`;

const SheetFooterRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 10px;
`;

// Destructive variant of the primary CTA — used on the disable confirm sheet.
const DestructiveButton = styled(PrimaryButton)`
  background: ${({ theme }) => r(theme).accent.alert};
  border-color: ${({ theme }) => r(theme).accent.alert};

  &:not(:disabled):hover {
    background: #d61420;
  }
`;

const DestructiveChevron = styled(ButtonChevron)`
  background: ${({ theme }) => r(theme).text.inverse};
  color: ${({ theme }) => r(theme).accent.alert};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
`;

interface AutoActivationToggleProps {
  scooterVin: string;
  onPreferenceChange?: (preferences: ScooterPreferences) => void;
}

const AutoActivationToggle: React.FC<AutoActivationToggleProps> = ({
  scooterVin,
  onPreferenceChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState<ScooterPreferences | null>(null);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scooterVin]);

  const loadPreferences = async () => {
    try {
      const prefs = await packService.getScooterPreferences(scooterVin);
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading preferences:', error);
      setPreferences({
        scooter_vin: scooterVin,
        auto_activation_enabled: true,
        next_pack_id: null,
      });
    }
  };

  const applyToggle = async (enabled: boolean) => {
    setLoading(true);
    try {
      const updatedPrefs = await packService.updateScooterPreferences(scooterVin, {
        auto_activation_enabled: enabled,
      });

      setPreferences(updatedPrefs);
      onPreferenceChange?.(updatedPrefs);

      Toast.show({
        content: enabled ? 'Auto-activation enabled' : 'Auto-activation disabled',
        position: 'bottom',
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
      Toast.show({
        content: 'Failed to update preferences',
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (enabled: boolean) => {
    if (!enabled) {
      setConfirmDisableOpen(true);
    } else {
      void applyToggle(true);
    }
  };

  const handleConfirmDisable = () => {
    setConfirmDisableOpen(false);
    void applyToggle(false);
  };

  if (!preferences) {
    return null;
  }

  return (
    <Container>
      <Header>
        <Title>
          Pack Auto-Activation
          <InfoTrigger
            onClick={() => setInfoOpen(true)}
            aria-label="What is pack auto-activation?"
          >
            <InformationCircleOutline style={{ fontSize: '15px' }} />
          </InfoTrigger>
        </Title>
        <ThemedSwitch
          checked={preferences.auto_activation_enabled}
          onChange={handleToggle}
          loading={loading}
          aria-label={`Pack auto-activation — ${
            preferences.auto_activation_enabled ? 'enabled' : 'disabled'
          }`}
        />
      </Header>
      <Description>
        Automatically activate the next pack when your current pack depletes.
      </Description>
      <StatusRow $enabled={preferences.auto_activation_enabled}>
        {preferences.auto_activation_enabled ? 'Enabled' : 'Disabled'}
      </StatusRow>

      <Popup
        visible={confirmDisableOpen}
        onMaskClick={() => setConfirmDisableOpen(false)}
        position="bottom"
        bodyStyle={{ background: 'transparent', padding: 0, boxShadow: 'none' }}
        destroyOnClose
      >
        <ThemeProvider theme={riderLight as never}>
          <Sheet>
            <DragHandle />
            <CloseButton onClick={() => setConfirmDisableOpen(false)}>×</CloseButton>
            <SheetEyebrow $tone="alert">Auto-activation · Disable</SheetEyebrow>
            <SheetTitle>Disable auto-activation?</SheetTitle>
            <SheetBody>
              When your current pack runs out, you'll need to manually activate the next pack.
              This may interrupt service.
            </SheetBody>
            <SheetFooter>
              <SheetFooterRow>
                <GhostButton onClick={() => setConfirmDisableOpen(false)}>
                  <span>Cancel</span>
                </GhostButton>
                <DestructiveButton onClick={handleConfirmDisable}>
                  <span>Disable</span>
                  <DestructiveChevron aria-hidden>→</DestructiveChevron>
                </DestructiveButton>
              </SheetFooterRow>
            </SheetFooter>
          </Sheet>
        </ThemeProvider>
      </Popup>

      <Popup
        visible={infoOpen}
        onMaskClick={() => setInfoOpen(false)}
        position="bottom"
        bodyStyle={{ background: 'transparent', padding: 0, boxShadow: 'none' }}
        destroyOnClose
      >
        <ThemeProvider theme={riderLight as never}>
          <Sheet>
            <DragHandle />
            <CloseButton onClick={() => setInfoOpen(false)}>×</CloseButton>
            <SheetEyebrow>How it works</SheetEyebrow>
            <SheetTitle>Pack auto-activation</SheetTitle>
            <SheetBody>
              When enabled, your next pack activates automatically as soon as your current pack
              runs out of energy or expires. When disabled, you'll have to activate each pack
              yourself.
            </SheetBody>
            <SheetFooter>
              <PrimaryButton onClick={() => setInfoOpen(false)}>
                <span>Got it</span>
                <ButtonChevron aria-hidden>→</ButtonChevron>
              </PrimaryButton>
            </SheetFooter>
          </Sheet>
        </ThemeProvider>
      </Popup>
    </Container>
  );
};

export default AutoActivationToggle;
